import { Button } from '@folio/components/ui/button';
import { cn, formatDate } from '@folio/lib';
import { ChevronLeft, ChevronRight, Trash2Icon, X } from 'lucide-react';
import type { DocumentViewerDocument } from './types';
import { useTranslation } from 'react-i18next';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text, Title } from '@folio/components/ui/text';

interface DocumentViewerHeaderProps {
  document: DocumentViewerDocument;
  currentIndex: number;
  totalDocuments: number;
  onNavigatePrev: () => void;
  onNavigateNext: () => void;
  canNavigatePrev: boolean;
  canNavigateNext: boolean;
  onDelete?: () => void;
  onClose?: () => void;
  className?: string;
}

export function DocumentViewerHeader({
  document,
  currentIndex,
  totalDocuments,
  onNavigatePrev,
  onNavigateNext,
  canNavigatePrev,
  canNavigateNext,
  onDelete,
  onClose,
  className,
}: DocumentViewerHeaderProps) {
  const { t } = useTranslation();

  const category = document.category ?? 'uncategorized';

  const displayIndex = currentIndex >= 0 ? currentIndex + 1 : 1;

  return (
    <div
      className={cn(
        'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 sm:grid-cols-[minmax(0,1fr)_auto_auto]',
        className,
      )}
    >
      <Stack gap={1} className="min-w-0">
        <Title order={3} size="md" tone="default" className="break-all leading-5">
          {document.name}
        </Title>
        <Group wrap gap={0} className="gap-x-2 gap-y-1 text-xs text-muted-foreground">
          <span>
            {t(`documentReview.category.${category}`, { defaultValue: t(`documents.${category}`) })}
          </span>
          {document.createdAt && (
            <>
              <span aria-hidden="true">·</span>
              <time
                dateTime={document.createdAt}
                aria-label={t('fileInput.uploadedAt', {
                  date: formatDate(new Date(document.createdAt)),
                })}
                className="whitespace-nowrap"
              >
                {formatDate(new Date(document.createdAt))}
              </time>
            </>
          )}
        </Group>
      </Stack>

      {onClose && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label={t('documentReview.closeViewer')}
          title={t('documentReview.closeViewer')}
          className="col-start-2 row-start-1 h-11 w-11 self-start rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 sm:col-start-3 sm:h-8 sm:w-8 sm:self-center"
        >
          <X className="h-4 w-4" />
        </Button>
      )}

      {(totalDocuments > 1 || onDelete) && (
        <Group
          justify="between"
          gap={3}
          className="col-span-2 row-start-2 sm:col-span-1 sm:col-start-2 sm:row-start-1"
        >
          {totalDocuments > 1 && (
            <Group gap={1}>
              <Button
                variant="ghost"
                size="icon"
                onClick={onNavigatePrev}
                disabled={!canNavigatePrev}
                aria-label={t('documentReview.previousDocument')}
                title={t('documentReview.previousDocument')}
                className="h-11 w-11 sm:h-8 sm:w-8 rounded-lg hover:bg-slate-100"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Text
                inline
                size="xs"
                weight="medium"
                tone="muted"
                className="min-w-10 text-center tabular-nums"
                aria-label={t('documentReview.documentPosition', {
                  current: displayIndex,
                  total: totalDocuments,
                })}
              >
                {displayIndex} / {totalDocuments}
              </Text>
              <Button
                variant="ghost"
                size="icon"
                onClick={onNavigateNext}
                disabled={!canNavigateNext}
                aria-label={t('documentReview.nextDocument')}
                title={t('documentReview.nextDocument')}
                className="h-11 w-11 sm:h-8 sm:w-8 rounded-lg hover:bg-slate-100"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </Group>
          )}
          {onDelete && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onDelete}
              aria-label={t('fileInput.deleteDocument')}
              title={t('fileInput.deleteDocument')}
              className="ml-auto h-11 w-11 sm:h-8 sm:w-8 rounded-lg text-slate-500 hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2Icon className="h-4 w-4" />
            </Button>
          )}
        </Group>
      )}
    </div>
  );
}
